import {
	isEpicHierarchyLevel,
	isBaseHierarchyLevel,
	isSubtaskHierarchyLevel,
} from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import { getProjectAvatarUrl } from '@atlassian/jira-project-settings-apps-common/src/avatar-service/avatar-normalizer.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { IssueTypeNavigationItem, RawIssueType } from '../../types.tsx';

type IssueTypeNavigationItemsCategories = {
	topLevel: IssueTypeNavigationItem[];
	baseLevel: IssueTypeNavigationItem[];
	bottomLevel: IssueTypeNavigationItem[];
};

const JSD_IS_EMAIL_REQUEST_TYPE = 'jsd.simplified.issue.type.isEmail';

/**
 * getIssueTypeNavigationItemsLevels is the original implementation of the selector
 * to allow for checking  hierarchy levels of issue types, containing an exception for
 * Jira service desk projects Email Request Type to render in the epic/top hierarchy level
 *
 * @param issueTypes A list of raw issue types
 * @returns IssueTypeNavigationItemsCategories
 */
const getIssueTypeNavigationItemsLevels = (issueTypes: RawIssueType[]) =>
	issueTypes.reduce(
		(acc: IssueTypeNavigationItemsCategories, issueType) => {
			const { id, avatarId, name, hierarchyLevel, properties, operations } = issueType;

			const issueTypeNavigationItem = {
				id,
				name,
				avatarId,
				operations,
				icon: getProjectAvatarUrl(avatarId),
			};
			if (
				isEpicHierarchyLevel(hierarchyLevel) ||
				(properties && properties[JSD_IS_EMAIL_REQUEST_TYPE])
			) {
				acc.topLevel.push(issueTypeNavigationItem);
			} else if (isBaseHierarchyLevel(hierarchyLevel)) {
				acc.baseLevel.push(issueTypeNavigationItem);
			} else if (isSubtaskHierarchyLevel(hierarchyLevel)) {
				acc.bottomLevel.push(issueTypeNavigationItem);
			}

			return acc;
		},
		{
			topLevel: [],
			baseLevel: [],
			bottomLevel: [],
		},
	);

/**
 * getTrueIssueTypeNavigationItemsLevels is a seperate implementation of the selector
 * to allow for checking true hierarchy levels of issue types not modified for nav levels
 * e.g. JSM email request type
 *
 * @param issueTypes A list of raw issue types
 * @returns IssueTypeNavigationItemsCategories
 */
const getTrueIssueTypeNavigationItemsLevels = (issueTypes: RawIssueType[]) =>
	issueTypes.reduce(
		(acc: IssueTypeNavigationItemsCategories, issueType) => {
			const { id, avatarId, name, hierarchyLevel, properties, operations } = issueType;

			const issueTypeNavigationItem = {
				id,
				name,
				avatarId,
				operations,
				icon: getProjectAvatarUrl(avatarId),
			};

			if (fg('jsm-request-type-bug-deletion')) {
				if (isEpicHierarchyLevel(hierarchyLevel)) {
					acc.topLevel.push(issueTypeNavigationItem);
				} else if (isBaseHierarchyLevel(hierarchyLevel)) {
					acc.baseLevel.push(issueTypeNavigationItem);
				} else if (isSubtaskHierarchyLevel(hierarchyLevel)) {
					acc.bottomLevel.push(issueTypeNavigationItem);
				}
			} else {
				// eslint-disable-next-line no-lonely-if
				if (
					isEpicHierarchyLevel(hierarchyLevel) ||
					(properties && properties[JSD_IS_EMAIL_REQUEST_TYPE])
				) {
					acc.topLevel.push(issueTypeNavigationItem);
				} else if (isBaseHierarchyLevel(hierarchyLevel)) {
					acc.baseLevel.push(issueTypeNavigationItem);
				} else if (isSubtaskHierarchyLevel(hierarchyLevel)) {
					acc.bottomLevel.push(issueTypeNavigationItem);
				}
			}

			return acc;
		},
		{
			topLevel: [],
			baseLevel: [],
			bottomLevel: [],
		},
	);

const getIsIssueTypeLimitReached = (limitPerProject: number | null, numberOfIssueTypes: number) =>
	limitPerProject !== null && limitPerProject > 0 && numberOfIssueTypes >= limitPerProject;

export {
	getIssueTypeNavigationItemsLevels,
	getTrueIssueTypeNavigationItemsLevels,
	getIsIssueTypeLimitReached,
};

export type { IssueTypeNavigationItemsCategories };
